import React, { useEffect, useState } from 'react';
import { Box, Link, List, ListItem, Typography } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useProviderLookup } from 'api/useProviderLookup';
import { useProviderSearch } from 'api/useProviderSearch';
import { Button } from 'components/Elements/Buttons/Button';
import { ConfirmPaymentDialog } from 'components/Elements/Dialogs/ConfirmPaymentDialog';
import { PhysicianSelect } from 'components/Elements/Inputs/PhysicianSelect';
import { ContentSection } from 'components/Elements/Layout/ContentSection';
import { PhysicianSearchCards } from 'components/Modules/DataDisplay/PhysicianSearchCards';
import { PhysicianResult, PaymentItem, PhysicianSearchGrid } from 'components/Modules/DataDisplay/PhysicianSearchGrid';
import { PageWrapper } from 'components/Modules/Wrappers/PageWrapper';
import { calculateTotal, fromApiBalance, isValidPaymentAmount } from 'utils/participation';
import { useDebounce } from 'utils/useDebounce';

export interface ProviderSearchProps {}

export interface PaymentData {
  paymentItems: Array<PaymentItem>;
  total: string;
}

const toPaymentItem = (item: PhysicianResult): PaymentItem => {
  const participation = fromApiBalance({
    amountDue: item.statusId === 6 && item.legalCanPay ? 0 : item.balance,
    participating: Boolean(item.participating),
  });

  return {
    ...item,
    balance: item.statusId === 6 && item.legalCanPay ? 'legal' : participation.balance,
    amount: item.statusId === 6 && item.legalCanPay ? '0' : participation.amount,
    participating: participation.participating,
    participationLocked: participation.participationLocked,
    baseBalance: participation.baseBalance,
  };
};

export const ProviderSearch: React.FC<ProviderSearchProps> = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { providers: provider } = useProviderLookup({
    id: +(searchParams.get('physicianId') ?? '0'),
  });

  const [rows, setRows] = React.useState<Array<PaymentItem & { isNew?: boolean }>>([]);
  const [ids, setIds] = useState<Array<string>>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [providerDismissed, setProviderDismissed] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedValue = useDebounce<string>(searchTerm, 500);
  const { results, loading } = useProviderSearch({ searchTerm: debouncedValue });

  const onPayNow = () => {
    setShowConfirmation(true);
  };

  const handleClick = (item: PhysicianResult) => {
    if (rows.length < 30) {
      setRows(oldRows => [...oldRows, toPaymentItem(item)]);
    } else {
      alert('You can only select up to 30 providers at a time.');
    }
  };

  const handleDeleteClick = (id: string) => {
    setRows(prevRows => prevRows.filter(row => row.id !== id));
    if (provider && id === provider.id) {
      setProviderDismissed(true);
    }
  };

  useEffect(() => {
    setIds(rows.map(x => x.id));
  }, [rows]);

  useEffect(() => {
    if (provider && !providerDismissed && !rows.find(x => x.id === provider.id)) {
      setRows(prevRows => [...prevRows, provider]);
    }
  }, [provider, providerDismissed, rows]);

  return (
    <PageWrapper>
      <ConfirmPaymentDialog
        open={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        handleConfirm={token => {
          const data = rows.map(item => ({
            ...item,
            amount: parseFloat(item.amount.toString()).toFixed(2),
          }));
          navigate('/payment', { state: { details: data, token } });
        }}
        items={rows.map(item => ({
          ...item,
          amount: parseFloat(item.amount.toString()).toFixed(2),
        }))}
      />
      <Typography mb={2} variant="h3" component="h1" fontSize={{ xs: 28, md: 36 }}>
        Provider Payment Portal
      </Typography>
      <Typography mb={2}>
        This Provider Payment Portal enables online payment of fees by credit card. For questions regarding the assessment, or issues
        related to submitting a payment, please contact the Program at (804) 330-2471.
      </Typography>
      <Typography mb={4}>
        If you believe you, or the physician you are paying for, qualify for an exemption, do NOT enter a payment at this time. Please visit{' '}
        <Link href="https://vabirthinjury.com/providers/exempt-providers/" target="_blank" rel="noopener noreferrer" color="primary">
          Exempt Providers
        </Link>{' '}
        to see if you qualify and access the{' '}
        <Link
          href="https://vabirthinjury.com/providers/provider-resources/physician-exemption-form/"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
        >
          Exemption Affidavit
        </Link>
        .
      </Typography>

      <ContentSection title="Search by Provider Name or License Number">
        <Typography mb={1}>
          Search by provider name or license number below to view and select a statement(s) for payment. You may add up to 30 providers for
          a single payment.
        </Typography>
        <List sx={{ listStyleType: 'disc', pl: 3, mb: 2, '& .MuiListItem-root': { display: 'list-item', py: 0.5 } }}>
          <ListItem disableGutters>
            <Typography>
              In order to participate, please search for and select the provider(s) using the search field below. Mark their record as
              participating by selecting the Participate button.
            </Typography>
          </ListItem>
          <ListItem disableGutters>
            <Typography>
              To become a participating provider, physicians must execute the Participation Agreement, pay the required annual participation
              fee, and agree to the conditions established by the Virginia Birth-Related Neurological Injury Compensation Act, including
              certain statutory obligations related to obstetrical care access and program oversight.
            </Typography>
          </ListItem>
          <ListItem disableGutters>
            <Typography>Coverage becomes effective 30 days after the Program receives both the signed agreement and payment.</Typography>
          </ListItem>
          <ListItem disableGutters>
            <Typography>
              If paying on behalf of multiple providers, search for and select each provider individually, and mark each provider as
              Participating, before selecting the Make Payment button.
            </Typography>
          </ListItem>
        </List>
        <PhysicianSelect
          open={open}
          setOpen={setOpen}
          handleClick={handleClick}
          selectedIds={[...ids]}
          results={results}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          loading={loading}
          sx={{ marginBottom: 0 }}
        />
      </ContentSection>

      <ContentSection title="Assessment Payments">
        <PhysicianSearchCards
          sx={{ display: { xs: 'block', md: 'none' } }}
          rows={rows}
          setRows={setRows}
          handleDeleteClick={handleDeleteClick}
        />
        <PhysicianSearchGrid
          sx={{
            display: { xs: 'none', md: 'flex' },
            minHeight: '230px',
            width: '100%',
          }}
          handleDeleteClick={handleDeleteClick}
          handleMailClick={() => {}}
          disableMail={false}
          rows={rows}
          setRows={setRows}
        />
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent={{ xs: 'space-between', md: 'end' }} mt={3} width="100%">
          <Typography display={{ xs: 'block', md: 'none' }} fontSize={22} fontWeight="bold">
            {`Total Amount: $${calculateTotal(rows)}`}
          </Typography>
          <Button
            disabled={rows.length === 0 || rows.some(row => !isValidPaymentAmount(row.amount, row))}
            type="button"
            size="medium"
            variant="contained"
            color="primary"
            onClick={onPayNow}
            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
          >
            Make Payment
          </Button>
        </Box>
      </ContentSection>
    </PageWrapper>
  );
};
